/**
 * Profile Detail — React harness.
 *
 * Wraps the pure reducer + effects in a `useReducer`-driven React hook.
 * The page wrapper passes a `navigateToChannelId` callback so the
 * Message CTA can route to /chat/:channelId after the chat seam
 * succeeds.
 *
 * Bucket C (ACT-INDEEP-FIGMA-DIRECT-CHAT-BACKEND-SLICE): the CTA is now
 * symmetric. The harness computes enabledness based on whether the
 * profile is loaded and whether the target profile is the viewer's
 * own profile (a UX affordance; the server authorizes the actual click).
 * The directional `viewerKind × variant` gating is REMOVED — the server
 * is authoritative for both directions.
 *
 * R8 (correction ACT): the harness no longer fires effects after
 * dispatch. It only RUNS the pending effects emitted by the
 * reducer. The navigation callback is part of the EffectContext,
 * not the effect payload.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  initialDetailModel,
  type ProfileDetailModel,
  type ProfileVariant,
} from './model';
import type { ProfileDetailMsg } from './messages';
import { update, type ProfileDetailEffect } from './update';
import { runEffect } from './effects';

export interface UseProfileDetailProgramArgs {
  readonly profileId: string;
  /**
   * The page wrapper determines the variant from the URL:
   *   /card/:id where id is a friend profile -> variant='friend'
   *   /card/:id where id is a sponsor profile -> variant='sponsor'
   *
   * Bucket C: variant is no longer used for CTA gating; the server
   * is authoritative. It IS still required by runLoadDetail to pick
   * the correct /api/v1/{friend|sponsor}/discovery/profiles/{id}
   * endpoint, until Profile Detail moves to a single endpoint.
   */
  readonly variant: ProfileVariant;
  /**
   * CORRECTION01 (R1 fix): the page wrapper supplies whether the
   * loaded profile is the authenticated viewer's own profile. The
   * harness combines this with detailLoaded to compute the CTA
   * enabledness (`detailLoaded && !isOwnProfile`) and dispatches
   * `CtaEnabledChanged` to the reducer on every change.
   *
   * The reducer cannot derive this from model alone because the
   * viewer's own profile id is environment, not application state.
   * The page wrapper is the only component that knows the
   * authenticated session's profile id.
   */
  readonly isOwnProfile: boolean;
  readonly navigateToChannelId: (channelId: string) => void;
}

export interface UseProfileDetailProgramResult {
  readonly model: ProfileDetailModel;
  readonly start: () => void;
  readonly retry: () => void;
  readonly clickMessageCta: () => void;
  readonly clearMessageCtaError: () => void;
}



/**
 * Bucket C: CTA enabledness is a UX affordance only.
 *
 * The CTA is shown whenever:
 *   - the profile is loaded (detail present), AND
 *   - the target profile is NOT the viewer's own profile.
 *
 * Authorization (chat eligibility, pairing, BOLA) is enforced
 * server-side in OpenPersonalChannel. The frontend never duplicates
 * pairing policy; it merely avoids a UI dead-end by hiding the CTA
 * when the user is looking at their own profile.
 *
 * `isOwnProfile` is supplied by the page wrapper, which compares
 * profileId against the authenticated account's profile id.
 */
function ctaEnabledFor(
  detailLoaded: boolean,
  isOwnProfile: boolean
): boolean {
  return detailLoaded && !isOwnProfile;
}

/**
 * CORRECTION04 (B-2.B): a stable identifier for a pending effect.
 * Used to deduplicate execution across React 18 StrictMode /
 * concurrent re-renders so the same effect is not run twice.
 */
function effectFingerprint(e: ProfileDetailEffect): string {
  switch (e.type) {
    case 'LoadDetail':
      return `LoadDetail:${e.profileId}:${e.variant}`;
    case 'OpenPersonalChat':
      return `OpenPersonalChat:${e.targetProfileId}`;
  }
}

export function useProfileDetailProgram(
  args: UseProfileDetailProgramArgs
): UseProfileDetailProgramResult {
  const { profileId, variant, isOwnProfile, navigateToChannelId } = args;
  // CORRECTION04 (B-2.B): useState + ref-based effect dedup. The
  // harness no longer uses a useEffect to drain pending effects;
  // instead, the dispatch wrapper runs each emitted effect
  // synchronously (in the same microtask as the dispatch), so
  // the effect is guaranteed to fire even under React 18 StrictMode
  // + concurrent rendering where a separate useEffect could see
  // a stale render closure and miss the emission (the J10 root
  // cause discovered in CORRECTION04).
  const [model, setModel] = useState<ProfileDetailModel>(() =>
    initialDetailModel(profileId, variant)
  );
  // CORRECTION05 (B-2.B): instance-scoped effect dedup. We
  // dedupe ONLY within a single dispatch invocation (the
  // "instance") to suppress StrictMode's same-tick double
  // invocation of reducers. The fingerprint set is NOT
  // permanent — it is cleared between dispatches. This allows
  // legitimate retries: a failed LoadDetail that the user
  // retries will re-fire with the same fingerprint but in a
  // NEW dispatch instance.
  //
  // The previous (CORRECTION04) implementation used a permanent
  // fingerprint cache that suppressed all retries — a real
  // regression risk flagged by the reviewer (R3).
  const instanceFingerprintsRef = useRef<Set<string>>(new Set());
  // CORRECTION04: dispatch is recreated on every model change so
  // the wrapper always sees the latest model. We also keep a ref to
  // the latest dispatch so user-facing callbacks (start, retry,
  // clickMessageCta, etc.) — which have stable identity — always
  // call the latest dispatch. This avoids the React 18
  // stale-closure bug where a stable useCallback captures the
  // first-render dispatch and dispatches against a stale model.
  // CORRECTION04 (B-2.B): stable dispatch via refs. The dispatch
  // is recreated on every model change, but user-facing callbacks
  // (start, retry, clickMessageCta, etc.) have stable identity
  // (empty deps) and must always dispatch against the LATEST model.
  //
  // We do this by holding `modelRef` and `dispatchRef` and resolving
  // them at call time. The dispatch function captures only the
  // refs, so it is itself stable across renders.
  const modelRef = useRef(model);
  modelRef.current = model;
  const dispatchRef = useRef<(msg: ProfileDetailMsg) => void>(() => {});
  const dispatch = useCallback(
    (msg: ProfileDetailMsg) => {
      const result = update(modelRef.current, msg);
      modelRef.current = result.model; // R4: same-tick stale-model fix
      setModel(result.model);
      // CORRECTION05 (R3): clear instance-scoped fingerprints at the
      // START of every dispatch. This is the key invariant:
      // dedup is per-dispatch-instance, not all-time. Legitimate
      // retries (a new dispatch) are NOT suppressed.
      instanceFingerprintsRef.current.clear();
      for (const effect of result.effects) {
        const fp = effectFingerprint(effect);
        if (instanceFingerprintsRef.current.has(fp)) continue;
        instanceFingerprintsRef.current.add(fp);
        runEffect(effect, {
          dispatch: dispatchRef.current,
          navigateToChannelId,
        });
      }
    },
    [navigateToChannelId]
  );
  // Keep the ref pointing at the latest dispatch on every render.
  dispatchRef.current = dispatch;

  // CORRECTION04: ctaEnabledFor effect — re-dispatch CtaEnabledChanged
  // whenever detail or isOwnProfile changes. We do NOT depend on
  // dispatch (which would cause an infinite loop); we use the
  // imperative dispatch wrapper directly.
  useEffect(() => {
    const detailLoaded = model.detail !== null;
    dispatch({
      type: 'CtaEnabledChanged',
      enabled: ctaEnabledFor(detailLoaded, isOwnProfile),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.detail, isOwnProfile]);

  // Pending effects are now driven synchronously by the dispatch
  // wrapper; callers only need `state.model`.
  const state = {
    model,
    pendingEffects: [] as readonly ProfileDetailEffect[],
  };

  const start = useCallback(() => dispatch({ type: 'Started' }), []);
  const retry = useCallback(
    () => dispatch({ type: 'RetryRequested' }),
    []
  );

  // R8 (correction ACT): the click message dispatches the user
  // intent; the pure reducer decides which effect to emit. The
  // harness never fires runEffect on its own from this callback.
  const clickMessageCta = useCallback(
    () => dispatch({ type: 'MessageCtaClicked' }),
    []
  );

  const clearMessageCtaError = useCallback(
    () => dispatch({ type: 'MessageCtaCleared' }),
    []
  );

  return {
    model: state.model,
    start,
    retry,
    clickMessageCta,
    clearMessageCtaError,
  };
}